/**
 * Detalle de licencia (M10) + acciones admin: suspender / reactivar.
 * Muestra el historial de eventos de pasarela (payment_events). La activación es
 * SIEMPRE webhook-driven; "reactivar" solo re-sincroniza sobre una suscripción
 * vigente (pagada) — no hay activación manual.
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminApi } from "../../api/endpoints.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import { Async, ConfirmDialog, fmtFecha, fmtFechaCorta } from "./ui.js";
import { LicenciaBadge, SubBadge } from "./AdminLicencias.js";
import type { TipoMovimientoCredito } from "../../api/types.js";
import s from "./admin.module.css";

const MOV_LABEL: Record<TipoMovimientoCredito, string> = {
  otorgamiento: "Otorgamiento",
  consumo: "Consumo",
  expiracion: "Expiración",
  ajuste: "Ajuste",
  backfill: "Backfill",
  asignacion_admin: "Asignación admin",
};

/** Saldo de evaluaciones del terapeuta + ledger + ajuste manual (auditado). */
function CreditosPanel({ terapeutaId }: { terapeutaId: string }) {
  const q = useQuery(() => adminApi.creditos(terapeutaId, { limit: 50 }), [terapeutaId]);
  const [ajustando, setAjustando] = useState(false);
  const [delta, setDelta] = useState("");
  const [motivo, setMotivo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function ajustar() {
    setError(null);
    const d = Number(delta);
    if (!Number.isInteger(d) || d === 0) return setError("Delta debe ser un entero distinto de 0.");
    if (!motivo.trim()) return setError("El motivo es obligatorio.");
    setOcupado(true);
    try {
      await adminApi.ajustarCreditos(terapeutaId, d, motivo.trim());
      setAjustando(false);
      setDelta("");
      setMotivo("");
      q.reload();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className={`${s.panel} ${s.panelPad}`} style={{ marginTop: 12 }}>
      <div className={s.toolbar} style={{ marginBottom: 8 }}>
        <div className={s.dt}>Créditos de evaluación</div>
        <span className={s.headerSpacer} />
        <button className={s.btn} onClick={() => setAjustando((v) => !v)}>
          Ajuste manual
        </button>
      </div>

      <Async q={q}>
        {(l) => (
          <>
            <div className={s.dl} style={{ marginBottom: 8 }}>
              <div>
                <div className={s.dt}>Saldo</div>
                <div className={s.dd}>
                  {l.saldo.ilimitado ? (
                    <span className={`${s.badge} ${s.badgePurple}`}>Ilimitado</span>
                  ) : (
                    <strong>{l.saldo.saldo}</strong>
                  )}
                </div>
              </div>
              <div>
                <div className={s.dt}>Cupo del plan</div>
                <div className={s.dd}>
                  {l.saldo.evaluacionesIncluidas === null ? "∞" : l.saldo.evaluacionesIncluidas}
                </div>
              </div>
            </div>

            {ajustando ? (
              <div className={`${s.banner} ${s.bannerInfo}`}>
                {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    className={s.search}
                    type="number"
                    placeholder="±delta (ej. +1, -1)"
                    value={delta}
                    onChange={(e) => setDelta(e.target.value)}
                    style={{ width: 130 }}
                  />
                  <input
                    className={s.search}
                    placeholder="Motivo (obligatorio)"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    style={{ flex: 1, minWidth: 180 }}
                  />
                  <button
                    className={`${s.btn} ${s.btnPrimary}`}
                    disabled={ocupado}
                    onClick={() => void ajustar()}
                  >
                    {ocupado ? "…" : "Aplicar"}
                  </button>
                </div>
              </div>
            ) : null}

            <div className={s.tableWrap} style={{ marginTop: 8 }}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Movimiento</th>
                    <th>Δ</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {l.movimientos.map((m) => (
                    <tr key={m.id}>
                      <td className={s.muted}>{fmtFecha(m.createdAt)}</td>
                      <td>{MOV_LABEL[m.tipo]}</td>
                      <td className={m.delta < 0 ? s.mono : s.mono}>
                        {m.delta > 0 ? `+${m.delta}` : m.delta}
                      </td>
                      <td className={s.muted}>
                        {m.motivo ?? (m.bateriaId ? `evaluación ${m.bateriaId.slice(0, 8)}` : m.origenTipo ?? "—")}
                      </td>
                    </tr>
                  ))}
                  {l.movimientos.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={s.empty}>
                        Sin movimientos de créditos.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Async>
    </div>
  );
}

type Accion = "suspender" | "reactivar";

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className={s.dt}>{label}</div>
      <div className={s.dd}>{children}</div>
    </div>
  );
}

export function AdminLicenciaDetail() {
  const { id = "" } = useParams();
  const q = useQuery(() => adminApi.licenciaDetalle(id), [id]);
  const [accion, setAccion] = useState<Accion | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function ejecutar() {
    if (!accion) return;
    setOcupado(true);
    setError(null);
    try {
      if (accion === "suspender") await adminApi.suspenderLicencia(id);
      else await adminApi.reactivarLicencia(id);
      setAccion(null);
      q.reload();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <Link to="/admin/creditos" className={s.backLink}>
        ‹ Volver a Créditos
      </Link>
      {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}

      <Async q={q}>
        {(l) => (
          <>
            <div className={s.sectionTitle}>
              {l.terapeutaNombre ?? "Terapeuta"}
              {l.activadaPorOffline ? (
                <span className={`${s.badge} ${s.badgePurple}`} style={{ marginLeft: 8 }}>
                  activada por pago offline
                </span>
              ) : null}
            </div>

            <div className={s.btnRow} style={{ marginBottom: 12 }}>
              {l.estadoLicencia === "activa" ? (
                <button className={`${s.btn} ${s.btnDanger}`} onClick={() => setAccion("suspender")}>
                  Suspender
                </button>
              ) : (
                <button className={s.btn} onClick={() => setAccion("reactivar")}>
                  Reactivar
                </button>
              )}
            </div>

            <div className={`${s.panel} ${s.panelPad}`}>
              <div className={s.dl}>
                <Campo label="Estado de licencia">
                  <LicenciaBadge estado={l.estadoLicencia} />
                </Campo>
                <Campo label="Estado de suscripción">
                  <SubBadge estado={l.estadoSuscripcion} />
                </Campo>
                <Campo label="Email">{l.terapeutaEmail ?? "—"}</Campo>
                <Campo label="Producto">
                  {l.productoNombre ?? l.productoCodigo ?? "—"}
                  {l.productoIntervalo ? <span className={s.muted}> · {l.productoIntervalo}</span> : null}
                </Campo>
                <Campo label="Precio">
                  {l.productoPrecio !== null ? `${l.productoPrecio} ${l.productoMoneda ?? ""}` : "—"}
                </Campo>
                <Campo label="Vence">
                  {fmtFechaCorta(l.currentPeriodEnd)}
                  {l.porVencer ? (
                    <span className={`${s.badge} ${s.badgeAmber}`} style={{ marginLeft: 6 }}>
                      &lt;7 días
                    </span>
                  ) : null}
                </Campo>
                <Campo label="Gracia hasta">{fmtFechaCorta(l.graciaHasta)}</Campo>
                <Campo label="Activada">{fmtFecha(l.activadaEn)}</Campo>
                <Campo label="Desactivada">{fmtFecha(l.desactivadaEn)}</Campo>
                <Campo label="Pasarela">
                  <span className={s.mono}>{l.gateway}</span>
                </Campo>
                <Campo label="Alta de suscripción">{fmtFecha(l.subCreatedAt)}</Campo>
              </div>
            </div>

            <div className={`${s.panel} ${s.panelPad}`} style={{ marginTop: 12 }}>
              <div className={s.dt}>Historial de pagos ({l.historialPagos.length})</div>
              <div className={s.tableWrap} style={{ marginTop: 8 }}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>Resultado</th>
                      <th>Pasarela</th>
                    </tr>
                  </thead>
                  <tbody>
                    {l.historialPagos.map((p) => (
                      <tr key={p.id}>
                        <td className={s.muted}>{fmtFecha(p.createdAt)}</td>
                        <td>{p.tipo}</td>
                        <td className={s.muted}>{p.resultado ?? "—"}</td>
                        <td className={s.mono}>{p.gateway}</td>
                      </tr>
                    ))}
                    {l.historialPagos.length === 0 ? (
                      <tr>
                        <td colSpan={4} className={s.empty}>
                          Sin eventos de pasarela registrados.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={`${s.panel} ${s.panelPad}`} style={{ marginTop: 12 }}>
              <div className={s.dt}>Pagos offline ({l.historialOffline.length})</div>
              <div className={s.tableWrap} style={{ marginTop: 8 }}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th>Fecha pago</th>
                      <th>Monto</th>
                      <th>Método</th>
                      <th>Referencia</th>
                      <th>Estado</th>
                      <th>Registró</th>
                      <th>Comprobante</th>
                    </tr>
                  </thead>
                  <tbody>
                    {l.historialOffline.map((p) => (
                      <tr key={p.id}>
                        <td className={s.muted}>{p.fechaPago}</td>
                        <td>
                          {p.monto.toFixed(2)} {p.moneda}
                        </td>
                        <td>{p.metodo}</td>
                        <td className={s.mono}>{p.referenciaOperacion ?? "—"}</td>
                        <td>
                          {p.estado === "registrado" ? (
                            <span className={`${s.badge} ${s.badgeGreen}`}>Registrado</span>
                          ) : (
                            <span className={`${s.badge} ${s.badgeNeutral}`} title={p.anuladoMotivo ?? ""}>
                              Anulado
                            </span>
                          )}
                        </td>
                        <td className={s.muted}>{p.registradoPorNombre ?? "—"}</td>
                        <td>
                          {p.comprobanteUrl ? (
                            <a className={s.btn} href={p.comprobanteUrl} target="_blank" rel="noreferrer">
                              Ver
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                    {l.historialOffline.length === 0 ? (
                      <tr>
                        <td colSpan={7} className={s.empty}>
                          Sin pagos offline en esta suscripción.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>

            <CreditosPanel terapeutaId={l.terapeutaId} />
          </>
        )}
      </Async>

      {accion ? (
        <ConfirmDialog
          titulo={accion === "suspender" ? "Suspender licencia" : "Reactivar licencia"}
          cuerpo={
            accion === "suspender" ? (
              <>¿Suspender esta licencia? El terapeuta perderá acceso. Queda auditado.</>
            ) : (
              <>
                ¿Reactivar? Solo funciona si la suscripción sigue vigente (pagada). No otorga acceso a
                una suscripción impaga.
              </>
            )
          }
          confirmar={accion === "suspender" ? "Suspender" : "Reactivar"}
          peligro={accion === "suspender"}
          ocupado={ocupado}
          onConfirm={() => void ejecutar()}
          onCancel={() => setAccion(null)}
        />
      ) : null}
    </>
  );
}
